import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Button, Divider, Stack, Tabs, Text } from '@mantine/core';
import {
    IconDownload,
    IconUsersGroup,
    IconChartPie,
    IconSettings,
    IconTags,
} from '@tabler/icons-react';
import { useApolloClient, useQuery } from '@apollo/client';

import { DragDrop } from '../../components/file-uploads/DragDrop';
import { HistoricalOverrideScheduledTable } from '../../components/file-uploads/HistoricalOverrideScheduledTable';
import { HistoricalOverrideActiveImportsTable } from '../../components/file-uploads/HistoricalOverrideActiveImportsTable';
import { HistoricalOverrideHistoryTable } from '../../components/file-uploads/HistoricalOverrideHistoryTable';
import { GET_HISTORICAL_OVERRIDE_SCHEDULED_IMPORTS } from '../../apollo/queries/getHistoricalOverrideScheduledImports';
import { GET_HISTORICAL_OVERRIDE_HISTORY } from '../../apollo/queries/getHistoricalOverrideHistory';
import { DashboardHeader } from '../../components/common';
import { ImportType } from '../../types/import';
import StoreModuleManager from './StoreModuleManager';
import AdvertisingZoneModuleManager from './AdvertisingZoneModuleManager';
import VirtualHierarchyManager from './VirtualHierarchyManager';
import { MaintenanceTabPanelHeader } from '../../components/common/MaintenanceTabPanelHeader';
import { GET_TEMPLATE_FILE } from '../../apollo/queries/getTemplateFile';
import { SalesReportDownloader } from '../../components/reports/SalesReportDownloader';
import { OfflineStoresReport } from '../../components/reports/OfflineStoresReport';
import { MemoryDiagnostics } from '../../components/diagnostics/MemoryDiagnostics';
import { AppConfigManager } from '../../components/config/AppConfigManager';
import { PriceSyncMonitor } from '../../components/price-sync/PriceSyncMonitor';
import { useAuth } from '../../context/AuthProvider';
import { checkUserRole } from '../../utility/userRoles';
import { UserRole } from '../../types/users';

type TemplateType = 'DAILY_SALES' | 'PRICE_GROUPS';

const DEFAULT_TAB = 'upload-sales';

const SUPER_ADMIN_TAB_VALUES = new Set([
    'store-modules',
    'advertising-zone-modules',
    'virtual-hierarchy',
    'app-config',
    'api-diagnostics',
    'price-sync',
]);

const VALID_TAB_VALUES = new Set([
    'upload-sales',
    'historical-manual-override',
    'download-sales',
    'ops-hierarchy',
    'upload-price-groups',
    'incomplete-sales-report',
    ...SUPER_ADMIN_TAB_VALUES,
]);

function isAllowedMaintenanceTab(tab: string, isSuperAdmin: boolean): boolean {
    if (!VALID_TAB_VALUES.has(tab)) {
        return false;
    }
    if (SUPER_ADMIN_TAB_VALUES.has(tab) && !isSuperAdmin) {
        return false;
    }
    return true;
}

function MaintenanceToolsPage() {
    const { authUser } = useAuth();
    const isSuperAdmin = checkUserRole(authUser, [UserRole.SUPER_ADMIN]);
    const apolloClient = useApolloClient();
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedTab = useMemo(() => {
        const requested = searchParams.get('tab');
        if (requested && isAllowedMaintenanceTab(requested, isSuperAdmin)) {
            return requested;
        }
        return DEFAULT_TAB;
    }, [searchParams, isSuperAdmin]);

    useEffect(() => {
        const requested = searchParams.get('tab');
        if (requested === selectedTab) {
            return;
        }
        const next = new URLSearchParams(searchParams);
        next.set('tab', selectedTab);
        setSearchParams(next, { replace: true });
    }, [searchParams, selectedTab, setSearchParams]);

    const handleTabChange = useCallback(
        (value: string | null) => {
            if (!value || !isAllowedMaintenanceTab(value, isSuperAdmin)) {
                return;
            }
            const next = new URLSearchParams(searchParams);
            next.set('tab', value);
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams, isSuperAdmin],
    );

    const [selectedTemplate, setSelectedTemplate] =
        React.useState<TemplateType | null>(null);
    const [hasActiveImport, setHasActiveImport] = useState(false);

    const refetchHistoricalLists = useCallback(() => {
        apolloClient
            .refetchQueries({
                include: [
                    GET_HISTORICAL_OVERRIDE_SCHEDULED_IMPORTS,
                    GET_HISTORICAL_OVERRIDE_HISTORY,
                ],
            })
            .then(() => undefined)
            .catch(() => undefined);
    }, [apolloClient]);

    const { data, error } = useQuery(GET_TEMPLATE_FILE, {
        variables: { inputType: selectedTemplate },
        fetchPolicy: 'network-only',
        skip: !selectedTemplate,
    });

    useEffect(() => {
        if (error) {
            setSelectedTemplate(null);
            return;
        }

        if (data?.getManualImportTemplateFile) {
            const { buffer, fileName } = data.getManualImportTemplateFile;
            try {
                const byteCharacters = atob(buffer);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i += 1) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } finally {
                setSelectedTemplate(null);
            }
        }
    }, [data, error]);

    const handleDownloadTemplate = (templateName: TemplateType) => {
        setSelectedTemplate(templateName);
    };

    return (
        <div className="relative">
            <DashboardHeader
                title="Maintenance Tools"
                hasBreadcrumb={false}
                hasDate
            />
            <div className="px-11 w-full mb-8">
                <Container size="xl" className="rounded-lg p-4">
                    <Tabs
                        color="blue"
                        value={selectedTab}
                        onChange={handleTabChange}
                        orientation="vertical"
                        className="flex w-full"
                        classNames={{
                            tab: 'text-gray-400 hover:bg-slate-100 data-[active]:text-[#00539f] data-[active]:font-medium',
                            tabLabel: 'text-left justify-start',
                        }}
                    >
                        <Tabs.List className="sticky top-24 self-start z-20 bg-light-grey max-h-[calc(100vh-6rem)] overflow-y-auto">
                            <Tabs.Tab value="upload-sales">
                                Upload Sales Data
                            </Tabs.Tab>
                            <Tabs.Tab value="historical-manual-override">
                                Daily Sales Historical Manual Override
                            </Tabs.Tab>
                            <Tabs.Tab value="download-sales">
                                Download Sales Data
                            </Tabs.Tab>
                            <Tabs.Tab value="ops-hierarchy">
                                Operations Hierarchy
                            </Tabs.Tab>
                            <Tabs.Tab value="upload-price-groups">
                                Upload Price Groups
                            </Tabs.Tab>
                            <Tabs.Tab value="incomplete-sales-report">
                                Incomplete Sales Report
                            </Tabs.Tab>
                            {isSuperAdmin && (
                                <Tabs.Tab value="store-modules">
                                    Store Modules
                                </Tabs.Tab>
                            )}
                            {isSuperAdmin && (
                                <Tabs.Tab value="advertising-zone-modules">
                                    Advertising Zone Management
                                </Tabs.Tab>
                            )}
                            {isSuperAdmin && (
                                <Tabs.Tab value="virtual-hierarchy">
                                    Virtual Hierarchy
                                </Tabs.Tab>
                            )}
                            {isSuperAdmin && (
                                <Tabs.Tab value="app-config">
                                    App Configuration
                                </Tabs.Tab>
                            )}
                            {isSuperAdmin && (
                                <Tabs.Tab value="api-diagnostics">
                                    API Diagnostics
                                </Tabs.Tab>
                            )}
                            {isSuperAdmin && (
                                <Tabs.Tab value="price-sync">
                                    Price Sync
                                </Tabs.Tab>
                            )}
                        </Tabs.List>

                        <div className="flex-1 min-w-0 bg-white rounded-lg rounded-t-none shadow-sm border border-gray-200 p-4">
                            <Tabs.Panel value="upload-sales">
                                <MaintenanceTabPanelHeader>
                                    <div className="flex items-center w-full">
                                        <div className="flex items-center gap-2">
                                            <IconChartPie size={24} />
                                            <span className="text-lg font-medium whitespace-nowrap">
                                                Upload Sales Data
                                            </span>
                                        </div>
                                        <div className="ml-auto flex items-center">
                                            <Button
                                                variant="subtle"
                                                leftSection={
                                                    <IconDownload size={16} />
                                                }
                                                onClick={() =>
                                                    handleDownloadTemplate(
                                                        'DAILY_SALES',
                                                    )
                                                }
                                            >
                                                Download Template
                                            </Button>
                                        </div>
                                    </div>

                                    <p className="text-sm text-gray-500 mt-2">
                                        Drag and drop your sales data file here
                                        or click to browse.
                                    </p>
                                </MaintenanceTabPanelHeader>

                                <DragDrop
                                    importType={ImportType.DailySales}
                                    title="Click or drag and drop your file here"
                                    description="Supported formats: XLSX, XLS, CSV"
                                />
                            </Tabs.Panel>

                            <Tabs.Panel value="historical-manual-override">
                                <MaintenanceTabPanelHeader>
                                    <div className="flex items-center justify-between h-12">
                                        <div className="flex items-center gap-2">
                                            <IconChartPie size={24} />
                                            <span className="text-lg font-medium">
                                                Daily Sales Historical Manual
                                                Override
                                            </span>
                                        </div>
                                    </div>

                                    <p className="text-sm text-gray-500 mt-2">
                                        Upload a workbook to override historical
                                        daily sales values. Each affected
                                        store/date becomes the latest
                                        close-of-business record for that day.
                                        Overdue-delivery values are inherited
                                        from the existing entry where available.
                                    </p>
                                    <p className="text-sm text-gray-500 mt-2">
                                        Expected file shape: four sheets named{' '}
                                        <strong>Nett Sales</strong>,{' '}
                                        <strong>Transactions</strong>,{' '}
                                        <strong>Nett GC Value</strong> and{' '}
                                        <strong>Nett GC %</strong>. Each sheet
                                        must have stores down rows (column A in
                                        the format{' '}
                                        <code>B013 - GABORONE NORTH</code>) and
                                        dates across columns (starting at column
                                        C). Any other sheets in the file are
                                        ignored.
                                    </p>
                                </MaintenanceTabPanelHeader>

                                <Stack gap="lg">
                                    <div>
                                        <Text
                                            size="sm"
                                            fw={600}
                                            c="dimmed"
                                            mb={6}
                                        >
                                            Schedule a new file
                                        </Text>
                                        <DragDrop
                                            importType={
                                                ImportType.DailySalesHistoricalManualOverride
                                            }
                                            title="Click or drag and drop your file here"
                                            description="Will be processed at 00:00 SAST tonight. Supported formats: XLSX, XLS"
                                            disabled={hasActiveImport}
                                            disabledReason="A historical import is currently being processed. Please wait for it to finish before scheduling another."
                                            onScheduled={refetchHistoricalLists}
                                        />
                                    </div>

                                    <HistoricalOverrideActiveImportsTable
                                        onActiveChange={setHasActiveImport}
                                    />

                                    <Divider />

                                    <div>
                                        <Text
                                            size="sm"
                                            fw={600}
                                            c="dimmed"
                                            mb={6}
                                        >
                                            Scheduled
                                        </Text>
                                        <HistoricalOverrideScheduledTable
                                            onChanged={refetchHistoricalLists}
                                        />
                                    </div>

                                    <div>
                                        <Text
                                            size="sm"
                                            fw={600}
                                            c="dimmed"
                                            mb={6}
                                        >
                                            History
                                        </Text>
                                        <HistoricalOverrideHistoryTable
                                            onChanged={refetchHistoricalLists}
                                        />
                                    </div>
                                </Stack>
                            </Tabs.Panel>

                            <Tabs.Panel value="download-sales">
                                <MaintenanceTabPanelHeader>
                                    <div className="flex items-center justify-between h-12">
                                        <div className="flex items-center gap-2">
                                            <IconDownload size={24} />
                                            <span className="text-lg font-medium">
                                                Download Sales Data
                                            </span>
                                        </div>
                                    </div>

                                    <p className="text-sm text-gray-500 mt-2">
                                        Select a date range to download sales
                                        data.
                                    </p>
                                </MaintenanceTabPanelHeader>

                                <SalesReportDownloader />
                            </Tabs.Panel>

                            <Tabs.Panel value="ops-hierarchy">
                                <MaintenanceTabPanelHeader>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <IconUsersGroup size={24} />
                                            <span className="text-lg font-medium">
                                                Upload Operations Hierarchy
                                            </span>
                                        </div>
                                    </div>

                                    <p className="text-sm text-gray-500 mt-2">
                                        Drag and drop your operations hierarchy
                                        file here or click to browse.
                                    </p>
                                </MaintenanceTabPanelHeader>

                                <DragDrop
                                    importType={ImportType.OpsHierarchy}
                                    title="Click or drag and drop your file here"
                                    description="Supported formats: XLSX, XLS, CSV"
                                />
                            </Tabs.Panel>

                            <Tabs.Panel value="upload-price-groups">
                                <MaintenanceTabPanelHeader>
                                    <div className="flex items-center w-full">
                                        <div className="flex items-center gap-2">
                                            <IconTags size={24} />
                                            <span className="text-lg font-medium whitespace-nowrap">
                                                Upload Price Groups
                                            </span>
                                        </div>
                                        <div className="ml-auto flex items-center">
                                            <Button
                                                variant="subtle"
                                                leftSection={
                                                    <IconDownload size={16} />
                                                }
                                                onClick={() =>
                                                    handleDownloadTemplate(
                                                        'PRICE_GROUPS',
                                                    )
                                                }
                                            >
                                                Download Template
                                            </Button>
                                        </div>
                                    </div>

                                    <p className="text-sm text-gray-500 mt-2">
                                        Drag and drop your price groups file
                                        here or click to browse.
                                    </p>
                                </MaintenanceTabPanelHeader>

                                <DragDrop
                                    importType={ImportType.PriceGroups}
                                    title="Click or drag and drop your file here"
                                    description="Supported formats: XLSX, XLS, CSV"
                                />
                            </Tabs.Panel>

                            <Tabs.Panel value="incomplete-sales-report">
                                <MaintenanceTabPanelHeader>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <IconChartPie size={24} />
                                            <span className="text-lg font-medium">
                                                Incomplete Sales Report
                                            </span>
                                        </div>
                                    </div>

                                    <p className="text-sm text-gray-500 mt-2">
                                        View stores with incomplete sales data
                                        (did not receive close of business
                                        message) for a selected date. Data may
                                        be received later.
                                    </p>
                                </MaintenanceTabPanelHeader>

                                <OfflineStoresReport />
                            </Tabs.Panel>

                            {isSuperAdmin && (
                                <Tabs.Panel value="store-modules">
                                    <MaintenanceTabPanelHeader>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <IconSettings size={24} />
                                                <span className="text-lg font-medium">
                                                    Manage Store Modules
                                                </span>
                                            </div>
                                        </div>

                                        <p className="text-sm text-gray-500 mt-2">
                                            Enable or disable modules for each
                                            store. Price Surveys can be toggled
                                            for zoneless stores; zoned stores
                                            use Advertising Zone Modules.
                                        </p>
                                    </MaintenanceTabPanelHeader>

                                    <StoreModuleManager />
                                </Tabs.Panel>
                            )}

                            {isSuperAdmin && (
                                <Tabs.Panel value="advertising-zone-modules">
                                    <MaintenanceTabPanelHeader>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <IconSettings size={24} />
                                                <span className="text-lg font-medium">
                                                    Manage Advertising Zone
                                                    Modules
                                                </span>
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-2">
                                            Enable or disable the Price Surveys
                                            module for each advertising zone.
                                            When disabled, users in that zone
                                            will not see Price Surveys.
                                        </p>
                                    </MaintenanceTabPanelHeader>
                                    <AdvertisingZoneModuleManager />
                                </Tabs.Panel>
                            )}

                            {isSuperAdmin && (
                                <Tabs.Panel value="virtual-hierarchy">
                                    <VirtualHierarchyManager />
                                </Tabs.Panel>
                            )}

                            {isSuperAdmin && (
                                <Tabs.Panel value="app-config">
                                    <AppConfigManager />
                                </Tabs.Panel>
                            )}

                            {isSuperAdmin && (
                                <Tabs.Panel value="api-diagnostics">
                                    <MemoryDiagnostics />
                                </Tabs.Panel>
                            )}

                            {isSuperAdmin && (
                                <Tabs.Panel value="price-sync">
                                    <PriceSyncMonitor />
                                </Tabs.Panel>
                            )}
                        </div>
                    </Tabs>
                </Container>
            </div>
        </div>
    );
}

export default MaintenanceToolsPage;
